import { API_BASE_URL } from '../../../../../config';
import type { JpAddressValue } from '../components/JpAddressInput';
import type { DeclarationSnapshot, HeadRelationship, YesNo } from './employeeSection/employeeSectionTypes';
import type {
  DisabilityCategory,
  NonResidentReason,
  SpouseDeclarationSnapshot,
  SpouseSnapshot,
} from './spouseSection/spouseSectionTypes';
import { parseIncomeYen, buildSpouseDeclarationFromApi } from './spouseSection/spouseEligibility';
import type { SpecialStatusSnapshot } from './specialStatus/specialStatusTypes';
import { specialStatusToApi } from './specialStatus/specialStatusMapper';
import type { OtherIncomeEarnerSnapshot } from './otherIncomeEarner/otherIncomeEarnerTypes';
import { otherIncomeEarnerToApi } from './otherIncomeEarner/otherIncomeEarnerMapper';
import type { RetirementAllowanceSnapshot } from './retirementAllowance/retirementAllowanceTypes';
import { retirementAllowanceToApi } from './retirementAllowance/retirementAllowanceMapper';

export type TaxSpecialStatusApi = {
  singleParentChildEmpFamilyId?: number | null;
  singleParentChildRelationship?: string | null;
  singleParentChildConfirmed?: boolean | null;
  singleParentClaimedByOther?: boolean | null;
  singleParentOutcome?: string | null;
  widowSpouseLost?: string | null;
  widowCurrentlyRemarried?: boolean | null;
  widowOutcome?: string | null;
  workingStudentEnrolled?: boolean | null;
  workingStudentSchoolType?: string | null;
  workingStudentSalaryOnly?: boolean | null;
  workingStudentSalaryRevenue?: number | string | null;
  workingStudentHasNonEmploymentIncome?: boolean | null;
  workingStudentNonEmploymentIncome?: number | string | null;
  workingStudentOutcome?: string | null;
  disabilityWorkingStudentChangedDuringYear?: boolean | null;
  /** YYYY-MM-DD */
  disabilityWorkingStudentChangeDate?: string | null;
};

export type TaxDeclarationProfile = {
  familyName?: string;
  firstName?: string;
  middleName?: string;
  familyNameKana?: string;
  firstNameKana?: string;
  middleNameKana?: string;
  dateOfBirth?: string;
  myNumber?: string;
  postalCode?: string;
  prefecture?: string;
  city?: string;
  streetAddress?: string;
  buildingName?: string;
  roomNumber?: string;
  maritalStatus?: 'married' | 'not_married';
};

export type SpouseSectionApi = {
  empFamilyId?: number | null;
  fullName?: string | null;
  fullNameKana?: string | null;
  myNumber?: string | null;
  dateOfBirth?: string | null;
  livingInSameHousehold?: boolean | null;
  ifNotAddress?: string | null;
  nonResidentFlag?: boolean | null;
  disabilityCategory?: DisabilityCategory | null;
  estimatedIncome?: number | string | null;
  withholdingFlag?: boolean | null;
  nonResidentReason?: string | null;
  changeDate?: string | null;
  changeReason?: string | null;
  sharedLivelihood?: boolean | null;
  familyWorker?: boolean | null;
  changeDuringYear?: boolean | null;
};

export type TaxDeclarationProgressPayload = {
  taxYear: number;
  formCode: string;
  currentStepId: string | null;
  sectionEmployeeDone: boolean;
  sectionSpouseDone: boolean;
  sectionDependentsDone: boolean;
  sectionSpecialDone: boolean;
  sectionOtherIncomeDone: boolean;
  profile: TaxDeclarationProfile;
  maritalStatus?: 'married' | 'not_married';
  spouse?: SpouseSectionApi | null;
  specialStatus?: TaxSpecialStatusApi | null;
  otherIncomeEarner?: {
    anyClaimedByOther?: boolean | null;
    entries?: Array<{
      empFamilyId?: number | null;
      memberFullName?: string | null;
      memberRelationship?: string | null;
      earnerRole?: string | null;
      earnerRoleOther?: string | null;
      earnerName?: string | null;
      memberToEarnerRelationship?: string | null;
      changedDuringYear?: boolean | null;
      changeDate?: string | null;
      changeReason?: string | null;
      changeReasonOther?: string | null;
    }> | null;
  } | null;
  retirementAllowance?: {
    anyExpected?: boolean | null;
    recipientCategory?: string | null;
    entries?: Array<{
      empFamilyId?: number | null;
      memberFullName?: string | null;
      memberRelationship?: string | null;
      retirementAllowanceAmount?: number | string | null;
    }> | null;
  } | null;
  declaration: {
    isHeadOfHousehold?: boolean | null;
    headOfHouseholdName?: string | null;
    relationshipToHead?: string | null;
    multipleEmployers?: boolean | null;
    employeeEstimatedIncome?: number | string | null;
    spouseAddedInSection?: boolean | null;
  };
};

export function profileToAddress(v: TaxDeclarationProfile): JpAddressValue {
  return {
    postalCode: v.postalCode ?? '',
    pref: v.prefecture ?? '',
    city: v.city ?? '',
    town: v.streetAddress ?? '',
    line2: [v.buildingName, v.roomNumber].filter(Boolean).join(' ').trim(),
  };
}

export function addressToProfilePatch(a: JpAddressValue): TaxDeclarationProfile {
  return {
    postalCode: a.postalCode.trim(),
    prefecture: a.pref.trim(),
    city: a.city.trim(),
    streetAddress: a.town.trim(),
    buildingName: a.line2.trim(),
    roomNumber: '',
  };
}

function ynToBool(v: YesNo | ''): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYn(v: boolean | null | undefined): YesNo | '' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

export function declarationFromApi(
  raw: TaxDeclarationProgressPayload['declaration']
): DeclarationSnapshot {
  return {
    isHeadOfHousehold: boolToYn(raw?.isHeadOfHousehold),
    headOfHouseholdName: raw?.headOfHouseholdName?.trim() ?? '',
    relationshipToHead: (raw?.relationshipToHead?.trim() as HeadRelationship) ?? '',
    multipleEmployers: boolToYn(raw?.multipleEmployers),
    employeeEstimatedIncome: incomeFromApi(raw?.employeeEstimatedIncome),
  };
}

export function declarationToApi(decl: DeclarationSnapshot) {
  const income = parseIncomeYen(decl.employeeEstimatedIncome);
  return {
    isHeadOfHousehold: ynToBool(decl.isHeadOfHousehold),
    headOfHouseholdName: decl.headOfHouseholdName.trim() || undefined,
    relationshipToHead: decl.relationshipToHead || undefined,
    multipleEmployers: ynToBool(decl.multipleEmployers),
    employeeEstimatedIncome: income > 0 ? income : undefined,
  };
}

function incomeFromApi(v: number | string | null | undefined): string {
  if (v == null) return '';
  if (typeof v === 'number') return String(Math.trunc(v));
  const trimmed = v.trim();
  if (!trimmed) return '';
  const n = Number(trimmed.replace(/,/g, ''));
  if (!Number.isFinite(n)) return trimmed.replace(/\D/g, '');
  return String(Math.trunc(n));
}

export function spouseFromApi(raw: SpouseSectionApi | null | undefined): SpouseSnapshot {
  if (!raw) {
    return emptySpouse();
  }
  const hasChange = Boolean(raw.changeDate?.trim() || raw.changeReason?.trim());
  const changeDuringYear =
    raw.changeDuringYear != null
      ? boolToYn(raw.changeDuringYear)
      : hasChange
        ? 'yes'
        : '';
  return {
    empFamilyId: raw.empFamilyId ?? null,
    fullName: raw.fullName?.trim() ?? '',
    fullNameKana: raw.fullNameKana?.trim() ?? '',
    myNumber: raw.myNumber?.trim() ?? '',
    dateOfBirth: raw.dateOfBirth?.trim() ?? '',
    livingInSameHousehold: boolToYn(raw.livingInSameHousehold),
    ifNotAddress: raw.ifNotAddress?.trim() ?? '',
    nonResidentFlag: boolToYn(raw.nonResidentFlag),
    nonResidentReason: (raw.nonResidentReason?.trim() as NonResidentReason) ?? '',
    disabilityCategory: (raw.disabilityCategory?.trim() as DisabilityCategory) ?? '',
    estimatedIncome: incomeFromApi(raw.estimatedIncome),
    changeDuringYear,
    changeDate: raw.changeDate?.trim() ?? '',
    changeReason: raw.changeReason?.trim() ?? '',
  };
}

export function spouseDeclarationFromApi(
  raw: TaxDeclarationProgressPayload['declaration'],
  spouseApi?: SpouseSectionApi | null
): SpouseDeclarationSnapshot {
  return buildSpouseDeclarationFromApi(raw, spouseApi);
}

export function emptySpouse(): SpouseSnapshot {
  return {
    empFamilyId: null,
    fullName: '',
    fullNameKana: '',
    myNumber: '',
    dateOfBirth: '',
    livingInSameHousehold: '',
    ifNotAddress: '',
    nonResidentFlag: '',
    nonResidentReason: '',
    disabilityCategory: '',
    estimatedIncome: '',
    changeDuringYear: '',
    changeDate: '',
    changeReason: '',
  };
}

export function spouseFamilyToApi(spouse: SpouseSnapshot) {
  return {
    id: spouse.empFamilyId ?? undefined,
    fullName: spouse.fullName.trim() || undefined,
    fullNameKana: spouse.fullNameKana.trim() || undefined,
    myNumber: spouse.myNumber.replace(/\D/g, '') || undefined,
    dateOfBirth: spouse.dateOfBirth.trim() || undefined,
    livingInSameHousehold: ynToBool(spouse.livingInSameHousehold),
    ifNotAddress: spouse.ifNotAddress.trim() || undefined,
    nonResidentFlag: ynToBool(spouse.nonResidentFlag),
    disabilityCategory: spouse.disabilityCategory || undefined,
  };
}

export function spouseTaxToApi(
  spouse: SpouseSnapshot,
  decl: SpouseDeclarationSnapshot,
  withholding: boolean
) {
  const income = parseIncomeYen(spouse.estimatedIncome);
  return {
    estimatedIncome: income > 0 ? income : undefined,
    withholdingFlag: withholding,
    sharedLivelihood: ynToBool(decl.sharedLivelihood),
    familyWorkerFlag: ynToBool(decl.spouseFamilyWorker),
    changeDuringYear: ynToBool(spouse.changeDuringYear),
    nonResidentReason: spouse.nonResidentReason || undefined,
    changeDate: spouse.changeDate.trim() || undefined,
    changeReason: spouse.changeReason.trim() || undefined,
  };
}

export function spouseDeclarationToApi(decl: SpouseDeclarationSnapshot) {
  const income = parseIncomeYen(decl.employeeEstimatedIncome);
  return {
    employeeEstimatedIncome: income > 0 ? income : undefined,
    spouseAddedInSection: decl.spouseAddedInSection || undefined,
  };
}

export async function fetchTaxDeclarationProgress(
  email: string,
  taxYear: number
): Promise<TaxDeclarationProgressPayload | null> {
  if (!API_BASE_URL || !email?.trim()) return null;
  try {
    const params = new URLSearchParams({ email: email.trim(), taxYear: String(taxYear) });
    const r = await fetch(`${API_BASE_URL}/api/tax-declaration/progress?${params.toString()}`);
    if (!r.ok) {
      console.warn('Tax declaration load failed', r.status);
      return null;
    }
    const data = (await r.json()) as TaxDeclarationProgressPayload;
    return data;
  } catch (e) {
    console.warn('Tax declaration load failed', e);
    return null;
  }
}

export async function upsertTaxDeclarationProgress(params: {
  email: string;
  taxYear: number;
  currentStepId?: string | null;
  sectionEmployeeDone?: boolean;
  sectionSpouseDone?: boolean;
  sectionDependentsDone?: boolean;
  sectionSpecialDone?: boolean;
  sectionOtherIncomeDone?: boolean;
  profilePatch?: TaxDeclarationProfile;
  declaration?: DeclarationSnapshot;
  specialStatus?: SpecialStatusSnapshot;
  otherIncomeEarner?: OtherIncomeEarnerSnapshot;
  retirementAllowance?: RetirementAllowanceSnapshot;
  spouseDeclaration?: SpouseDeclarationSnapshot;
  spouseFamilyPatch?: SpouseSnapshot;
  spouseTaxPatch?: { spouse: SpouseSnapshot; decl: SpouseDeclarationSnapshot; withholding: boolean };
}): Promise<TaxDeclarationProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim()) return null;
  try {
    const body: Record<string, unknown> = {
      email: params.email.trim(),
      taxYear: params.taxYear,
      formCode: 'FF006',
    };
    if (params.currentStepId !== undefined) body.currentStepId = params.currentStepId;
    if (params.sectionEmployeeDone !== undefined) body.sectionEmployeeDone = params.sectionEmployeeDone;
    if (params.sectionSpouseDone !== undefined) body.sectionSpouseDone = params.sectionSpouseDone;
    if (params.sectionDependentsDone !== undefined) body.sectionDependentsDone = params.sectionDependentsDone;
    if (params.sectionSpecialDone !== undefined) body.sectionSpecialDone = params.sectionSpecialDone;
    if (params.sectionOtherIncomeDone !== undefined) body.sectionOtherIncomeDone = params.sectionOtherIncomeDone;
    if (params.profilePatch) body.profilePatch = params.profilePatch;
    if (params.declaration) body.declaration = declarationToApi(params.declaration);
    if (params.specialStatus) body.specialStatusPatch = specialStatusToApi(params.specialStatus);
    if (params.otherIncomeEarner) body.otherIncomeEarnerPatch = otherIncomeEarnerToApi(params.otherIncomeEarner);
    if (params.retirementAllowance) body.retirementAllowancePatch = retirementAllowanceToApi(params.retirementAllowance);
    if (params.spouseDeclaration) {
      body.declaration = {
        ...(body.declaration as object | undefined),
        ...spouseDeclarationToApi(params.spouseDeclaration),
      };
    }
    if (params.spouseFamilyPatch) {
      body.spouseFamilyPatch = spouseFamilyToApi(params.spouseFamilyPatch);
    }
    if (params.spouseTaxPatch) {
      body.spouseTaxPatch = spouseTaxToApi(
        params.spouseTaxPatch.spouse,
        params.spouseTaxPatch.decl,
        params.spouseTaxPatch.withholding
      );
    }

    const r = await fetch(`${API_BASE_URL}/api/tax-declaration/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      console.warn('Tax declaration save failed', r.status);
      return null;
    }
    return (await r.json()) as TaxDeclarationProgressPayload;
  } catch (e) {
    console.warn('Tax declaration save failed', e);
    return null;
  }
}
